import { useCallback, useEffect, useState } from 'react'

import { notificationsApi, type UserNotification } from '@/api/notifications'
import { useTenantFeatures } from '@/hooks'
import { runAfterFirstPaint } from '@/shared/hooks/useLocaleRefetch'

import { filterNotificationsByFeatures } from '../lib/notification-filter'

const POLL_MS = 60_000
const PREVIEW_LIMIT = 50

export function useUnreadNotificationCount() {
  const { features } = useTenantFeatures()
  const [count, setCount] = useState(0)

  const refresh = useCallback(async () => {
    try {
      const list = await notificationsApi.list({
        unread_only: true,
        limit: PREVIEW_LIMIT,
        offset: 0,
      })
      const visible = filterNotificationsByFeatures(list.items, features)
      setCount(visible.length)
    } catch {
      /* ignore polling errors */
    }
  }, [features])

  useEffect(() => {
    runAfterFirstPaint(() => void refresh())
    const timer = window.setInterval(() => void refresh(), POLL_MS)
    return () => window.clearInterval(timer)
  }, [refresh])

  return { count, refresh }
}

export function useNotificationPreview() {
  const { features } = useTenantFeatures()
  const [items, setItems] = useState<UserNotification[]>([])
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const list = await notificationsApi.list({ limit: 8, offset: 0 })
      setItems(filterNotificationsByFeatures(list.items, features))
    } catch {
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [features])

  return { items, loading, load }
}
